import React, { useState } from 'react';
import { Upload, CheckCircle, XCircle, Loader } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface UploadStatus {
  filename: string;
  status: 'uploading' | 'success' | 'error';
  message?: string;
}

export default function UploadPage() {
  const [uploads, setUploads] = useState<UploadStatus[]>([]);
  const [isDragging, setIsDragging] = useState(false);

  const handleFileUpload = async (files: FileList) => {
    const fileArray = Array.from(files);
    const pdfFiles = fileArray.filter(file => file.type === 'application/pdf');

    if (pdfFiles.length === 0) {
      alert('Please select PDF files only');
      return;
    }

    for (const file of pdfFiles) {
      const uploadStatus: UploadStatus = {
        filename: file.name,
        status: 'uploading'
      };

      setUploads(prev => [...prev, uploadStatus]);

      try {
        const { error } = await supabase.storage
          .from('publications')
          .upload(file.name, file, {
            cacheControl: '3600',
            upsert: true
          });

        if (error) throw error;

        setUploads(prev =>
          prev.map(u =>
            u.filename === file.name
              ? { ...u, status: 'success', message: 'Uploaded successfully' }
              : u
          )
        );
      } catch (error) {
        setUploads(prev =>
          prev.map(u =>
            u.filename === file.name
              ? { ...u, status: 'error', message: error.message }
              : u
          )
        );
      }
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileUpload(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Upload Publications</h1>

        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
            isDragging
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-300 bg-white'
          }`}
        >
          <Upload className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <p className="text-lg text-gray-700 mb-2">
            Drag and drop PDF files here
          </p>
          <p className="text-sm text-gray-500 mb-4">or</p>
          <label className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg cursor-pointer hover:bg-blue-700 transition-colors">
            Choose Files
            <input
              type="file"
              multiple
              accept=".pdf"
              className="hidden"
              onChange={(e) => e.target.files && handleFileUpload(e.target.files)}
            />
          </label>
        </div>

        {uploads.length > 0 && (
          <div className="mt-8 bg-white rounded-lg shadow overflow-hidden">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Upload Status</h2>
            </div>
            <ul className="divide-y divide-gray-200">
              {uploads.map((upload, index) => (
                <li key={index} className="p-4 flex items-center justify-between">
                  <div className="flex items-center flex-1 min-w-0">
                    {upload.status === 'uploading' && (
                      <Loader className="w-5 h-5 text-blue-500 animate-spin mr-3 flex-shrink-0" />
                    )}
                    {upload.status === 'success' && (
                      <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                    )}
                    {upload.status === 'error' && (
                      <XCircle className="w-5 h-5 text-red-500 mr-3 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {upload.filename}
                      </p>
                      {upload.message && (
                        <p className={`text-sm ${
                          upload.status === 'error' ? 'text-red-600' : 'text-gray-500'
                        }`}>
                          {upload.message}
                        </p>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-blue-900 mb-2">Important:</h3>
          <p className="text-sm text-blue-800">
            Upload your PDF files with the exact filenames referenced in your publications data:
          </p>
          <ul className="mt-2 text-sm text-blue-800 list-disc list-inside space-y-1">
            <li><code className="bg-blue-100 px-1 rounded">TEST PUBLICATION.pdf</code></li>
            <li><code className="bg-blue-100 px-1 rounded">THE DYNAMICS OF HONOUR IN VIOLENCE.pdf</code></li>
          </ul>
        </div>
      </div>
    </div>
  );
}
